import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface PremiumBannerProps {
  show: boolean;
  onClose?: () => void;
  variant?: 'toast' | 'modal' | 'top';
}

export const PremiumBanner: React.FC<PremiumBannerProps> = ({ 
  show, 
  onClose,
  variant = 'top' 
}) => {
  const [isVisible, setIsVisible] = useState(show);

  useEffect(() => {
    setIsVisible(show);
  }, [show]);

  if (variant === 'toast') {
    return (
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ 
              duration: 0.3, 
              ease: [0.25, 0.1, 0.25, 1] 
            }}
            style={{
              position: 'fixed',
              top: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 9999,
              maxWidth: '90vw',
              width: '380px',
            }}
          >
            <div
              style={{
                background: 'rgba(14, 14, 14, 0.92)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                borderRadius: '16px',
                padding: '14px 18px',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                boxShadow: `
                  inset 0 1px 0 rgba(255, 255, 255, 0.06),
                  0 8px 24px rgba(0, 0, 0, 0.5),
                  0 0 0 1px rgba(201, 162, 77, 0.08)
                `,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'linear-gradient(145deg, rgba(201, 162, 77, 0.15), rgba(201, 162, 77, 0.05))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    flexShrink: 0,
                  }}
                >
                  🔒
                </div>
                <div style={{ flex: 1 }}>
                  <p
                    style={{
                      margin: 0,
                      fontSize: '14px',
                      fontWeight: '500',
                      color: 'rgba(255, 255, 255, 0.95)',
                      lineHeight: '1.4',
                    }}
                  >
                    Сначала подпишитесь на канал
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  if (variant === 'modal') {
    return (
      <AnimatePresence>
        {isVisible && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.75)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                zIndex: 9998,
              }}
              onClick={onClose}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ 
                duration: 0.3, 
                ease: [0.25, 0.1, 0.25, 1] 
              }}
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 9999,
                maxWidth: '90vw',
                width: '360px',
              }}
            >
              <div
                style={{
                  background: 'linear-gradient(145deg, #0E0E0E 0%, #141414 100%)',
                  backdropFilter: 'blur(20px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                  borderRadius: '24px',
                  padding: '32px 24px',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  boxShadow: `
                    inset 0 1px 0 rgba(255, 255, 255, 0.06),
                    0 16px 48px rgba(0, 0, 0, 0.6)
                  `,
                }}
              >
                <div
                  style={{
                    width: '56px',
                    height: '56px',
                    margin: '0 auto 20px',
                    borderRadius: '50%',
                    background: 'linear-gradient(145deg, rgba(201, 162, 77, 0.2), rgba(201, 162, 77, 0.05))',
                    border: '1px solid rgba(201, 162, 77, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '28px',
                  }}
                >
                  🔒
                </div>
                <h3
                  style={{
                    margin: '0 0 12px',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: 'rgba(255, 255, 255, 0.95)',
                    textAlign: 'center',
                    letterSpacing: '-0.01em',
                  }}
                >
                  Подписка обязательна
                </h3>
                <p
                  style={{
                    margin: '0 0 24px',
                    fontSize: '15px',
                    fontWeight: '400',
                    color: 'rgba(255, 255, 255, 0.65)',
                    textAlign: 'center',
                    lineHeight: '1.5',
                  }}
                >
                  Для доступа к функциям приложения необходимо подписаться на канал
                </p>
                <button
                  onClick={onClose}
                  style={{
                    width: '100%',
                    padding: '14px',
                    borderRadius: '14px',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    background: 'linear-gradient(145deg, #1a1a1a 0%, #0e0e0e 100%)',
                    color: 'rgba(255, 255, 255, 0.95)',
                    fontSize: '15px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-out',
                    boxShadow: `
                      inset 0 1px 0 rgba(255, 255, 255, 0.06),
                      0 4px 12px rgba(0, 0, 0, 0.3)
                    `,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.01)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = 'scale(0.98)';
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = 'scale(1.01)';
                  }}
                >
                  Понятно
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -60 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -60 }}
          transition={{ 
            duration: 0.4, 
            ease: [0.25, 0.1, 0.25, 1] 
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            padding: '16px',
          }}
        >
          <div
            style={{
              maxWidth: '600px',
              margin: '0 auto',
              background: 'linear-gradient(145deg, #0E0E0E 0%, #141414 100%)',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              borderRadius: '16px',
              padding: '16px 20px',
              border: '1px solid rgba(201, 162, 77, 0.15)',
              boxShadow: `
                inset 0 1px 0 rgba(255, 255, 255, 0.06),
                0 8px 24px rgba(0, 0, 0, 0.5),
                0 0 0 1px rgba(201, 162, 77, 0.08)
              `,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'linear-gradient(145deg, rgba(201, 162, 77, 0.2), rgba(201, 162, 77, 0.08))',
                  border: '1px solid rgba(201, 162, 77, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  flexShrink: 0,
                  boxShadow: 'inset 0 1px 2px rgba(201, 162, 77, 0.1)',
                }}
              >
                🔒
              </div>
              <div style={{ flex: 1 }}>
                <p
                  style={{
                    margin: '0 0 2px',
                    fontSize: '15px',
                    fontWeight: '600',
                    color: 'rgba(255, 255, 255, 0.95)',
                    letterSpacing: '-0.01em',
                  }}
                >
                  Подписка обязательна
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: '13px',
                    fontWeight: '400',
                    color: 'rgba(255, 255, 255, 0.65)',
                    lineHeight: '1.4',
                  }}
                >
                  Сначала подпишитесь на канал Headman
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
